/**
 * Anchored popovers for the embed's dropdowns and info tips, built on
 * the HTML Popover API (`popover="auto"`).
 *
 * Why the top layer: an absolutely-positioned dropdown is clipped by
 * any `overflow: hidden` / `overflow: auto` ancestor — an accordion,
 * the drilldown modal's scroll body, a customer's card — and trapped
 * by host-page transforms. A popover renders above all of that, and
 * `auto` brings light-dismiss and Esc for free. Being in the top layer
 * it is positioned against the viewport, so placement is computed in
 * script from the anchor's rect.
 *
 * Toggling is declarative where supported: the trigger carries
 * `popovertarget`, which lets the browser handle "click the trigger
 * of an open popover" without the light-dismiss-then-reopen race a
 * click handler would hit. Callers keep a click-handler fallback for
 * browsers without the API (`supported === false`).
 */

import { onBeforeUnmount, onMounted, ref, type Ref } from "vue";

export interface Rect {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface AnchoredPosition {
  top: number;
  left: number;
  maxHeight: number;
  placement: "below" | "above";
}

export interface AnchoredOptions {
  /** Gap between anchor and popover, px. */
  gap?: number;
  /** Minimum distance from the viewport edge, px. */
  margin?: number;
  /** Height the popover would like, px — decides above vs below. */
  preferredHeight?: number;
  /** Hard cap on the popover's height, px; it scrolls past this. Unset =
   *  as tall as the room allows. */
  maxHeight?: number;
}

/**
 * Place a popover below its anchor, or above when there is more room
 * there and not enough below. `left` follows the anchor's left edge
 * but is clamped so the popover never leaves the viewport.
 */
export function computeAnchoredPosition(
  anchor: Rect,
  pop: Size,
  viewport: Size,
  { gap = 4, margin = 8, preferredHeight = 240, maxHeight = Infinity }: AnchoredOptions = {},
): AnchoredPosition {
  const below = viewport.height - anchor.bottom - gap - margin;
  const above = anchor.top - gap - margin;
  const want = Math.min(pop.height, preferredHeight, maxHeight);
  const placement = below >= want || below >= above ? "below" : "above";
  const room = Math.max(0, Math.min(maxHeight, placement === "below" ? below : above));
  const height = Math.min(pop.height, room);
  const top = placement === "below" ? anchor.bottom + gap : anchor.top - gap - height;
  const maxLeft = viewport.width - pop.width - margin;
  const left = Math.max(margin, Math.min(anchor.left, maxLeft));
  return { top: Math.round(top), left: Math.round(left), maxHeight: Math.floor(room), placement };
}

/** True where `HTMLElement.prototype.showPopover` exists. */
export const popoverSupported = (): boolean =>
  typeof HTMLElement !== "undefined" &&
  typeof (HTMLElement.prototype as { showPopover?: unknown }).showPopover === "function";

const viewportSize = (): Size => {
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  return vv
    ? { width: vv.width, height: vv.height }
    : { width: window.innerWidth, height: window.innerHeight };
};

export interface AnchoredPopover {
  /** Whether the popover is showing. Synced from the `toggle` event. */
  open: Ref<boolean>;
  /** Popover API available; when false callers render a fallback. */
  supported: boolean;
  /** Re-run placement (after content changes size). */
  reposition: () => void;
  hide: () => void;
}

/**
 * Wire positioning + open state for `popover` element `pop` anchored to
 * `anchor`. `sheet()` returning true skips anchoring — the caller's
 * CSS lays the popover out as a bottom sheet instead.
 */
export function useAnchoredPopover(
  anchor: Ref<HTMLElement | null>,
  pop: Ref<HTMLElement | null>,
  opts: AnchoredOptions & { sheet?: () => boolean } = {},
): AnchoredPopover {
  const open = ref(false);
  const supported = popoverSupported();

  const reposition = (): void => {
    const el = pop.value;
    const a = anchor.value;
    if (!el || !a || !open.value) return;
    if (opts.sheet?.()) {
      el.style.removeProperty("top");
      el.style.removeProperty("left");
      el.style.removeProperty("max-height");
      return;
    }
    const rect = a.getBoundingClientRect();
    el.style.setProperty("--anchor-w", `${Math.round(rect.width)}px`);
    const pos = computeAnchoredPosition(
      rect,
      { width: el.offsetWidth, height: el.scrollHeight },
      viewportSize(),
      opts,
    );
    el.style.top = `${pos.top}px`;
    el.style.left = `${pos.left}px`;
    el.style.maxHeight = `${pos.maxHeight}px`;
    el.dataset.placement = pos.placement;
  };

  let frame: number | null = null;
  const schedule = (): void => {
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      reposition();
    });
  };

  const listen = (on: boolean): void => {
    const method = on ? "addEventListener" : "removeEventListener";
    // Capture: any ancestor scroller (the page, a modal body) moves
    // the anchor.
    window[method]("scroll", schedule, { capture: true, passive: true } as never);
    window[method]("resize", schedule);
    window.visualViewport?.[method]("resize", schedule);
  };

  const onToggle = (e: Event): void => {
    const next = (e as Event & { newState?: string }).newState === "open";
    open.value = next;
    listen(next);
    if (next) {
      // Measure after the browser has laid the popover out.
      reposition();
      schedule();
    }
  };

  onMounted(() => pop.value?.addEventListener("toggle", onToggle));
  onBeforeUnmount(() => {
    pop.value?.removeEventListener("toggle", onToggle);
    listen(false);
    if (frame !== null) cancelAnimationFrame(frame);
  });

  const hide = (): void => {
    const el = pop.value as (HTMLElement & { hidePopover?: () => void }) | null;
    if (supported && el?.matches(":popover-open")) el.hidePopover?.();
    else open.value = false;
  };

  return { open, supported, reposition, hide };
}
